import { ActionTree, ActionContext } from 'vuex'
import CONSTANTS from '@/constants'
import {
  fetchSubscriptions,
  postUpdatePaymentMethod,
  postCancelSubscription,
  postReactivateSubscription,
  fetchTermExtensionPricing,
  putOneTermToSubscription,
  fetchInvoices,
  fetchInvoiceDownload,
  getAvailablePlans,
  fetchPassTypes,
  fetchPlanComparison,
  putUpgradePlan,
  fetchPaymentInfo,
  fetchSubscriptionCancellationFields,
  postSubscriptionCancellationReasons,
} from '../../../models/subscriptions'
import { SubscriptionsState } from './state'
import { Plan } from '../../types'

const actions: ActionTree<SubscriptionsState, {}> = {
  getSubscriptions: async (context: ActionContext<SubscriptionsState, {}>) => {
    try {
      const subscriptions = await fetchSubscriptions()
      context.commit('CURRENT_SUBSCRIPTIONS_UPDATED', subscriptions)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  updatePaymentMethod: async (
    context: ActionContext<SubscriptionsState, {}>,
    token: string
  ) => {
    try {
      const { success } = await postUpdatePaymentMethod(token)
      if (success) {
        context.dispatch(
          'app/setNotification',
          "You've successfully updated your payment method!",
          { root: true }
        )
      }
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  cancelSubscription: async (
    context: ActionContext<SubscriptionsState, {}>,
    id: string
  ) => {
    try {
      await postCancelSubscription(id)
      const subscriptions = await fetchSubscriptions()
      context.commit('CURRENT_SUBSCRIPTIONS_UPDATED', subscriptions)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },

  reactivateSubscription: async (
    context: ActionContext<SubscriptionsState, {}>,
    id: string
  ) => {
    try {
      await postReactivateSubscription(id)
      const subscriptions = await fetchSubscriptions()
      context.commit('CURRENT_SUBSCRIPTIONS_UPDATED', subscriptions)
      context.dispatch(
        'app/setNotification',
        "You've successfully reactivated the renewal on the subscription.",
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getTermExtensionPricing: async (
    context: ActionContext<SubscriptionsState, {}>,
    id: string
  ) => {
    try {
      const pricing = await fetchTermExtensionPricing(id)
      context.commit('CURRENT_ONE_TERM_PRICING_UPDATED', pricing)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  addOneTermToSubscription: async (
    context: ActionContext<SubscriptionsState, {}>,
    id: string
  ) => {
    try {
      await putOneTermToSubscription(id)
      const subscriptions = await fetchSubscriptions()
      context.commit('CURRENT_SUBSCRIPTIONS_UPDATED', subscriptions)
      context.dispatch(
        'app/setNotification',
        "You've successfully extended the subscription",
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getAllInvoices: async (context: ActionContext<SubscriptionsState, {}>) => {
    try {
      const invoices = await fetchInvoices()
      context.commit('CURRENT_INVOICES_UPDATED', invoices)
    } catch (error) {
      return error
    }
  },

  downloadInvoice: async (
    context: ActionContext<SubscriptionsState, {}>,
    id: string
  ) => {
    try {
      await fetchInvoiceDownload(id)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  getPassTypes: async (context: ActionContext<SubscriptionsState, {}>) => {
    try {
      const passes = await fetchPassTypes()
      context.commit('PASS_TYPES_UPDATED', passes)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
    }
  },

  fetchAvailablePlans: async (
    context: ActionContext<SubscriptionsState, {}>
  ) => {
    try {
      const plans: { [key: string]: Plan } = await getAvailablePlans()
      const availablePlans = Object.entries(plans).map(p => {
        const current = p[1]
        if (current.plan_code === CONSTANTS.PLANS.MONTHLY) {
          current.term = 'month'
        } else {
          current.term = 'year'
        }
        return current
      })
      const monthly = availablePlans.find(
        p => p.plan_code === CONSTANTS.PLANS.MONTHLY
      )
      const annual = availablePlans.find(
        p => p.plan_code === CONSTANTS.PLANS.ANNUAL
      )
      const premium = availablePlans.find(
        p => p.plan_code === CONSTANTS.PLANS.PREMIUM
      )
      if (monthly && annual && premium) {
        context.commit('AVAILABLE_PLANS_UPDATED', [monthly, annual, premium])
      } else {
        context.commit('AVAILABLE_PLANS_UPDATED', availablePlans.reverse())
      }
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },

  getPlanComparison: async (
    context: ActionContext<SubscriptionsState, {}>,
    idAndPlanCode
  ) => {
    try {
      const comparison = await fetchPlanComparison(idAndPlanCode)
      context.commit('PLAN_COMPARISON_UPDATED', comparison)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },

  upgradePlan: async (
    context: ActionContext<SubscriptionsState, {}>,
    idAndPlanCode
  ) => {
    try {
      const plan = await putUpgradePlan(idAndPlanCode)
      context.commit('SUBSCRIPTION_UPGRADED', plan)
      context.dispatch(
        'app/setNotification',
        'Your upgrade is being processed and should be complete momentarily. Please check back later. A new invoice will be sent to your email',
        { root: true }
      )
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },

  getPaymentInfo: async (context: ActionContext<SubscriptionsState, {}>) => {
    try {
      const {
        account_credit,
        payment_method_mask,
        payment_method_object,
      }: {
        account_credit: string
        payment_method_mask: string
        payment_method_object: string
      } = await fetchPaymentInfo()
      context.commit('PAYMENT_INFO_UPDATED', {
        account_credit,
        payment_method_mask,
        payment_method_object,
      })
    } catch (error) {
      return error
    }
  },

  getCancellationFields: async (
    context: ActionContext<SubscriptionsState, {}>
  ) => {
    try {
      const survey = await fetchSubscriptionCancellationFields()
      context.commit('CANCELLATION_FIELDS_UPDATED', survey)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },

  sendCancellationResponses: async (
    context: ActionContext<SubscriptionsState, {}>,
    payload: object
  ) => {
    try {
      await postSubscriptionCancellationReasons(payload)
    } catch (error) {
      const { message } = error
      context.dispatch('app/setError', message, { root: true })
      throw error
    }
  },
}

export default actions
